/*
  SubroutineItemForm.jsx
*/

// Npm.
import React, { useMemo } from 'react';

// Components.
import Grid from '../../../Grid';
import ButtonBase from '../../../ButtonBase';
import TextFieldOnBlur from '../../../TextFieldOnBlur';
import IconButton from '../../../IconButton';
import CheckboxWithLabel from '../../../CheckboxWithLabel';
import NodeSelector from '../../../NodeSelector';
import LinkSelector from '../../../LinkSelector';

// Icons.
import RemoveIcon from '../../../../icons/Close';
import AddIcon from '../../../../icons/Add';
import UpIcon from '../../../../icons/ArrowUpward';
import DownIcon from '../../../../icons/ArrowDownward';

// makes.
import makeStyles from '../../../../makes/makeStyles';

// Styles.
const useStyles = makeStyles(theme => ({
  titleContainer: {
    position: 'relative',
    '& > .titleButton': {
      padding: theme.space(2, 0, 2, 2),
      color: theme.color_primary_main,
      textAlign: 'left',
      borderLeft: '2px solid transparent',
    },
    '&.expanded': {
      marginBottom: theme.space(2),
      '& > .titleButton': {
        borderLeft: `2px solid ${theme.color_primary_main}`,
      },
    },
  },
  titleButtonContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
  },
  formContainer: {
    paddingLeft: theme.space(2),
    borderLeft: `2px solid ${theme.color_primary_main}`,
  },
}));

// Constants.
const DEFAULT_FIELDS = {
  method: '',
  narration: '',
  batch: false,
  included_edges: [],
  excluded_edges: [],
  included_source_nodes: [],
  excluded_source_nodes: [],
  included_target_nodes: [],
  excluded_target_nodes: [],
};

/**
 * Section for editing "graphData._botech.subroutines" data.
 */
export default React.memo(function SubroutineItemForm({
  className,
  index = 0,
  subroutine,
  nodes = [],
  links = [],
  disabled = false,
  expanded = false,
  onToggleExpand,
  onMoveUp,
  onMoveDown,
  onChange,
  onAddBelow,
  onRemove,
  ...rest
}) {

  const classes = useStyles();

  // Handlers.

  function handleClickExpand() {
    if (onToggleExpand) {
      onToggleExpand((!expanded && subroutine?._id) || null);
    }
  }

  function handleMoveUp() {
    onMoveUp(subroutine);
  }

  function handleMoveDown() {
    onMoveDown(subroutine);
  }

  function handleRemove() {
    onRemove(subroutine);
  }

  function handleAddBelow() {
    onAddBelow(subroutine);
  }

  function changeFields(next) {
    if (onChange) {
      onChange(subroutine, { ...subroutine, ...next });
    }
  }

  // Form fields with defaults.

  const fields = {
    ...DEFAULT_FIELDS,
    ...subroutine,
  };

  // Classes.

  let titleContainerClass = classes.titleContainer;
  if (expanded) {
    titleContainerClass += ' expanded';
  }

  return (
    <div className={className}>

      <div className={titleContainerClass}>
        <ButtonBase
          className="titleButton overline"
          fullWidth
          onClick={handleClickExpand}
        >
          { `SUBROUTINE ${index + 1}` }
        </ButtonBase>
        <div className={classes.titleButtonContainer}>
          {
            Boolean(onMoveUp) &&
            <IconButton
              title="Move Up"
              aria-label="move up"
              onClick={handleMoveUp}
              disabled={disabled}
            >
              <UpIcon />
            </IconButton>
          }
          {
            Boolean(onMoveDown) &&
            <IconButton
              title="Move Down"
              aria-label="move down"
              onClick={handleMoveDown}
              disabled={disabled}
            >
              <DownIcon />
            </IconButton>
          }
          {
            Boolean(onAddBelow) &&
            <IconButton
              title="Add Below"
              aria-label="add below"
              onClick={handleAddBelow}
              disabled={disabled}
            >
              <AddIcon />
            </IconButton>
          }
          {
            Boolean(onRemove) &&
            <IconButton
              title="Remove"
              aria-label="remove"
              onClick={handleRemove}
              disabled={disabled}
            >
              <RemoveIcon />
            </IconButton>
          }
        </div>
      </div>

      {
        expanded &&
        <div className={classes.formContainer}>
          <Grid className="fadeIn" container spacing={2} align="flex-end">
            <Grid xs={9}>

              <TextFieldOnBlur
                label="Method"
                placeholder="required"
                value={fields.method}
                fullWidth
                maxLength={128}
                disabled={disabled}
                onChange={(method) => changeFields({ method })}
              />

            </Grid>
            <Grid xs={3}>

              <CheckboxWithLabel
                className={classes.checkbox}
                fullWidth
                label="Batch"
                checked={fields.batch}
                onChange={(event) => changeFields({ batch: event.target.checked })}
              />

            </Grid>
            <Grid xs={12}>

              <TextFieldOnBlur
                label="Narration"
                value={fields.narration}
                fullWidth
                maxLength={128}
                disabled={disabled}
                onChange={(narration) => changeFields({ narration })}
              />
              
            </Grid>
            <Grid xs={12}>

              <LinkSelector
                label="Included Edges"
                multiple
                fullWidth
                value={fields.included_edges}
                links={links}
                disabled={disabled}
                onChange={(included_edges) => changeFields({ included_edges })}
              />

            </Grid>
            <Grid xs={12}>

              <LinkSelector
                label="Excluded Edges"
                multiple
                fullWidth
                value={fields.excluded_edges}
                links={links}
                disabled={disabled}
                onChange={(excluded_edges) => changeFields({ excluded_edges })}
              />

            </Grid>
            <Grid xs={12}>

              <NodeSelector
                label="Included Source Nodes"
                multiple
                fullWidth
                value={fields.included_source_nodes}
                nodes={nodes}
                disabled={disabled}
                onChange={(included_source_nodes) => changeFields({ included_source_nodes })}
              />

            </Grid>
            <Grid xs={12}>

              <NodeSelector
                label="Excluded Source Nodes"
                multiple
                fullWidth
                value={fields.excluded_source_nodes}
                nodes={nodes}
                disabled={disabled}
                onChange={(excluded_source_nodes) => changeFields({ excluded_source_nodes })}
              />

            </Grid>
            <Grid xs={12}>

              <NodeSelector
                label="Included Target Nodes"
                multiple
                fullWidth
                value={fields.included_target_nodes}
                nodes={nodes}
                disabled={disabled}
                onChange={(included_target_nodes) => changeFields({ included_target_nodes })}
              />

            </Grid>
            <Grid xs={12}>

              <NodeSelector
                label="Excluded Target Nodes"
                multiple
                fullWidth
                value={fields.excluded_target_nodes}
                nodes={nodes}
                disabled={disabled}
                onChange={(excluded_target_nodes) => changeFields({ excluded_target_nodes })}
              />

            </Grid>
          </Grid>
        </div>
      }

    </div>
  );
});
